import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Toast } from 'primeng/toast';
import { Observable } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { AttachmentItem } from '../attachment-item/attachment-item';
import { CardComponent } from '../card/card.component';
import { deleteConfirmationDialog } from '../confirmation-dialogs/confirmation-dialogs-presets';
import { DropZone } from '../drop-zone/drop-zone';
import { DocumentAttachment, DocumentSlot } from './document-upload.types';

interface UploadingEntry {
  tempId: string;
  fileName: string;
}

@Component({
  selector: 'lib-document-upload',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService, MessageService],
  imports: [
    CardComponent,
    AttachmentItem,
    DropZone,
    ConfirmDialogModule,
    Toast,
    ButtonModule,
  ],
  // The confirmation dialog carries its own footer so that it can outlive the
  // click that accepted it. PrimeNG's default buttons call `onAccept()`, which
  // runs the callback and hides the dialog in the same tick — so the DELETE was
  // still in flight while the confirmation was already gone, and the row it was
  // about to remove sat there looking untouched. These buttons close it when the
  // request answers.
  template: `
    <p-confirmDialog>
      <ng-template #footer>
        <p-button
          label="Annulla"
          severity="secondary"
          styleClass="min-w-30 p-button-text"
          [disabled]="deleting()"
          (onClick)="cancelRemove()"
        />
        <p-button
          label="Elimina"
          severity="danger"
          styleClass="min-w-30"
          [loading]="deleting()"
          (onClick)="confirmRemove()"
        />
      </ng-template>
    </p-confirmDialog>
    <p-toast />
    <lib-card [attr.data-tour-slot]="slot().id">
      <ng-container title>
        {{ slot().label }}
        @if (slot().required) {
          <span class="ml-0.5 text-red-500" title="Obbligatorio">*</span>
        }
      </ng-container>

      @if (slot().description) {
        <ng-container subtitle>{{ slot().description }}</ng-container>
      }

      <ng-container actions>
        @if (!readonly()) {
          <input
            type="file"
            #fileInput
            [multiple]="slot().multiple !== false"
            accept="*"
            hidden
            (change)="onFilePicked($event, fileInput)"
          />
          <p-button
            label="Aggiungi"
            icon="pi pi-plus"
            severity="secondary"
            size="small"
            data-tour="document-add"
            (onClick)="fileInput.click()"
          />
        }
      </ng-container>
      <ng-container content>
        <ng-content select="[slot-meta]" />
        @if (readonly()) {
          <!-- No drop zone and no per-file actions: the list is all there is. -->
          @if (value().length === 0) {
            <div
              class="flex flex-col items-center justify-center gap-2 py-8 text-slate-400"
            >
              <i class="pi pi-file text-4xl opacity-40"></i>
              <span>Nessun documento caricato</span>
            </div>
          } @else {
            <ul class="flex flex-col gap-1 p-2">
              @for (file of value(); track file.id) {
                <lib-attachment-item
                  [fileName]="file.fileName"
                  [downloadUrl]="file.url"
                  [showDownload]="true"
                  [showReindex]="false"
                  [indexingStatus]="file.indexingStatus"
                  [indexingError]="file.indexingError"
                />
              }
            </ul>
          }
        } @else {
          <lib-drop-zone
            data-tour="document-dropzone"
            (dropped)="onDrop($event)"
          >
            @if (value().length === 0 && uploading().length === 0) {
              <div
                class="flex flex-col items-center justify-center gap-2 py-8 text-slate-400"
              >
                <i class="pi pi-cloud-upload text-4xl opacity-40"></i>
                <span class=""
                  >Trascina un file qui oppure usa il pulsante</span
                >
              </div>
            } @else {
              <ul class="flex flex-col gap-1 p-2">
                @for (file of value(); track file.id) {
                  <lib-attachment-item
                    [fileName]="file.fileName"
                    [downloadUrl]="file.url"
                    [showDownload]="true"
                    [showDelete]="true"
                    [indexingStatus]="file.indexingStatus"
                    [indexingError]="file.indexingError"
                    (deleteClick)="remove(file.id)"
                    (reindexClick)="reindex(file)"
                  />
                }
                @for (entry of uploading(); track entry.tempId) {
                  <lib-attachment-item
                    [fileName]="entry.fileName"
                    [loading]="true"
                  />
                }
              </ul>
            }
          </lib-drop-zone>
        }
      </ng-container>
    </lib-card>
  `,
})
export class DocumentUploadComponent {
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);

  readonly slot = input.required<DocumentSlot>();
  /**
   * May emit more than once: the first emission is the uploaded file, later ones are the same
   * attachment (same id) as its indexing progresses. Each replaces the row it updates.
   */
  readonly uploadFn =
    input.required<(file: File) => Observable<DocumentAttachment>>();
  readonly deleteFn =
    input<(file: DocumentAttachment) => Observable<unknown>>();
  /** May emit more than once, like {@link uploadFn}. */
  readonly reindexFn =
    input<(file: DocumentAttachment) => Observable<DocumentAttachment>>();
  readonly value = model<DocumentAttachment[]>([]);
  /**
   * Show the files, allow nothing else: no add button, no drop zone, no
   * delete, no re-index. Downloads stay.
   */
  readonly readonly = input(false);

  protected readonly uploading = signal<UploadingEntry[]>([]);

  /** The attachment the open confirmation is about, and whether it is going. */
  protected readonly deleting = signal(false);
  private pendingDelete: string | null = null;

  protected onDrop(event: DragEvent): void {
    const files = event.dataTransfer?.files;
    if (files) this.uploadAll(files);
  }

  protected onFilePicked(event: Event, input: HTMLInputElement): void {
    const files = (event.target as HTMLInputElement).files;
    if (files) this.uploadAll(files);
    input.value = '';
  }

  protected remove(attachmentId: string): void {
    this.pendingDelete = attachmentId;
    this.deleting.set(false);

    deleteConfirmationDialog(this.confirmationService, {
      message: 'Sei sicuro di voler eliminare questo allegato?',
      // No `accept`: the footer template drives both buttons, so that the
      // dialog can stay up while the request runs.
      //
      // Escape is off for the same reason. There is no ✕ either
      // (`closable: false`), so this dialog is dismissed by its own buttons and
      // nothing else — which is what lets "Elimina" mean "it is gone".
      closeOnEscape: false,
    });
  }

  protected cancelRemove(): void {
    if (this.deleting()) return;

    this.pendingDelete = null;
    this.confirmationService.close();
  }

  protected confirmRemove(): void {
    const attachmentId = this.pendingDelete;
    if (attachmentId === null || this.deleting()) return;

    const deleteFn = this.deleteFn();
    const attachment = this.value().find((f) => f.id === attachmentId);

    // Nothing to call, or nothing to call it about: drop the row and close. The
    // uncontrolled case (no `deleteFn`) is the parent keeping the list itself.
    if (!deleteFn || !attachment) {
      if (deleteFn === undefined) {
        this.value.update((current) =>
          current.filter((f) => f.id !== attachmentId),
        );
      }

      this.closeRemove();
      return;
    }

    this.deleting.set(true);

    deleteFn(attachment).subscribe({
      next: () => {
        this.value.update((current) =>
          current.filter((f) => f.id !== attachmentId),
        );
        this.closeRemove();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: `Eliminazione di "${attachment.fileName}" fallita. Riprovare.`,
          life: 5000,
        });
        this.closeRemove();
      },
    });
  }

  private closeRemove(): void {
    this.deleting.set(false);
    this.pendingDelete = null;
    this.confirmationService.close();
  }

  protected reindex(attachment: DocumentAttachment): void {
    const reindexFn = this.reindexFn();
    if (!reindexFn) return;

    this.value.update((current) =>
      current.map((f) =>
        f.id === attachment.id ? { ...f, indexingStatus: 'InProgress' } : f,
      ),
    );

    reindexFn(attachment)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.value.update((current) =>
            current.map((f) => (f.id === updated.id ? updated : f)),
          );
        },
        error: () => {
          this.value.update((current) =>
            current.map((f) =>
              f.id === attachment.id
                ? { ...f, indexingStatus: attachment.indexingStatus }
                : f,
            ),
          );
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: `Reindicizzazione di "${attachment.fileName}" fallita. Riprovare.`,
            life: 5000,
          });
        },
      });
  }

  private uploadAll(files: FileList): void {
    const allowMultiple = this.slot().multiple !== false;

    if (!allowMultiple && this.value().length > 0) {
      this.value.set([]);
    }

    const count = allowMultiple ? files.length : 1;
    for (let i = 0; i < count; i++) {
      this.uploadOne(files[i]);
    }
  }

  private uploadOne(file: File): void {
    const tempId = uuid();
    this.uploading.update((list) => [...list, { tempId, fileName: file.name }]);

    const dropPlaceholder = () =>
      this.uploading.update((list) => list.filter((e) => e.tempId !== tempId));

    let added = false;

    this.uploadFn()(file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // The placeholder goes at the first emission, not at completion: the stream stays open while
        // indexing is followed, and by then the real row — with its own badge — is on screen. Only
        // that first emission adds a row; later ones update it, so a file deleted while its indexing
        // is still being followed does not come back with the next poll.
        next: (result) => {
          dropPlaceholder();
          this.value.update((current) =>
            added
              ? current.map((f) => (f.id === result.id ? result : f))
              : [...current, result],
          );
          added = true;
        },
        complete: dropPlaceholder,
        error: () => {
          dropPlaceholder();
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: `Caricamento di "${file.name}" fallito. Riprovare.`,
            life: 5000,
          });
        },
      });
  }
}
